import useAppMutation from 'hooks/useAppMutation';
import useNotify from 'hooks/useNotify';
import { useTranslation } from 'react-i18next';
import { BaseErrorResponse } from 'models/_base';
import API from 'services/API';
import usePropertySettingsInitData from '../usePropertySettingsInitData';
import usePropertySettingsQuery from '../usePropertySettingsQuery';
import { UnitListSchemaType } from './PropertySettingsUnitsTab.schema';

export const UNIT_SUBMIT_MUTATION_KEY = ['unitSubmit'];

const useUnitSubmit = () => {
  const {
    property: { uid: propertyUid },
  } = usePropertySettingsInitData();
  const { invalidateQuery } = usePropertySettingsQuery();
  const { t } = useTranslation();
  const { notifyError } = useNotify();

  const { mutate: updateUnits } = useAppMutation({
    mutationKey: UNIT_SUBMIT_MUTATION_KEY,
    mutationFn: async (units: UnitListSchemaType['units']) => {
      const unitsPayload = units?.map((unit) =>
        unit.isTemporary
          ? {
              name: unit.name,
              active: unit.isActive,
            }
          : {
              uid: unit.uid,
              name: unit.name,
              active: unit.isActive,
            },
      );

      const response = await API.post(`/v3/units`, {
        propertyUid,
        units: unitsPayload,
      });
      return response.data;
    },
    onError: (error: BaseErrorResponse) => {
      if (
        error.response?.data?.apiErrorMessage.includes(
          'This unit has bookings. It can be de-activated',
        )
      ) {
        notifyError(
          t('pageProperty.units.modals.delete.error.unitHasBookings'),
        );
      } else {
        notifyError(t('pageProperty.units.modals.delete.error.default'));
      }
    },
    onSettled: () => {
      invalidateQuery();
    },
  });

  const handleSaveUnits = (units: UnitListSchemaType['units']) => {
    updateUnits(units);
  };

  return { handleSaveUnits };
};

export default useUnitSubmit;
